import {
  Record,
  String as RTString,
  Boolean as RTBoolean,
  Static,
  Array as RTArray,
  Number as RTNumber,
  Partial
} from 'runtypes';
import { DeepPartial } from 'utility-types';
import find from 'lodash.find';
import map from 'lodash.map';
import { buildService } from './decorated-service';
import config from '../../../config';

const CheckRemittanceFormServiceArgs = Record({
  customer: Partial({
    firstName: RTString,
    middleName: RTString,
    lastName: RTString
  }),
  trust: Partial({
    name: RTString,
    trustDate: RTString,
    taxID: RTString,
    taxIDSSN: RTBoolean,
    address: Partial({
      street1: RTString,
      street2: RTString,
      street3: RTString,
      state: RTString,
      city: RTString,
      country: RTString,
      zip: RTString,
      usaddress: RTBoolean,
      militaryAddress: RTBoolean,
      foreignAddress: RTBoolean
    })
  }),
  address: Partial({
    street1: RTString,
    city: RTString,
    state: RTString,
    zip: RTString
  }),
  accountType: RTString,
  fundSelections: RTArray(
    Partial({
      fundName: RTString,
      accountNumber: RTString,
      recordKeepingCode: RTString,
      percentageAmount: RTNumber,
      dollarAmount: RTNumber,
      contributionYear: RTString
    })
  )
});

export type StaticGetCheckRemittanceFormArgs = Static<
  typeof CheckRemittanceFormServiceArgs
>;

export type DeepPartialMutualFundFormArgs = DeepPartial<
  StaticGetCheckRemittanceFormArgs
>;

const CheckRemittanceFormService = buildService(
  '/nasu-svc/service/check-remittance-form'
);

export const postCheckRemittanceFormData = (
  args: DeepPartialMutualFundFormArgs
): Promise<any> => {
  return CheckRemittanceFormService.add(args, {
    responseType: 'application/pdf',
    headers: {
      'content-type': 'application/json',
      'content-disposition': 'inline',
      apikey: config('apiKey'),
      'x-trp-requested-with': 'XMLHttpRequest'
    },
    ignoreServiceFailure: true,
    transformResponse: resp => resp.blob()
  });
};

export const getAccountNumberByTRPCode = (trpCode, accounts): any => {
  const account = find(accounts, accountObj => {
    return (
      accountObj.trpCode === trpCode ||
      (accountObj.investmentVehicle &&
        accountObj.investmentVehicle.trpCode === trpCode)
    );
  });
  return account ? account.accountNumber : '';
};

export const getFundNameByTRPCode = (trpCode, accounts): any => {
  const account = find(accounts, accountObj => {
    return (
      accountObj.trpCode === trpCode ||
      (accountObj.investmentVehicle &&
        accountObj.investmentVehicle.trpCode === trpCode)
    );
  });
  return account ? account.name : '';
};

export const getRKCodeByTRPCode = (trpCode, details): any => {
  const fund = find(details, fundObj => {
    return (
      fundObj.trpCode === trpCode ||
      (fundObj.investmentVehicle &&
        fundObj.investmentVehicle.trpCode === trpCode)
    );
  });
  return fund ? fund.recordKeepingCode : '';
};

/* eslint-disable complexity */
export const parseInvestmentsForRemittanceForm: any = (
  investments,
  details,
  accounts,
  isFundLaterFlow
) => {
  if (!investments) {
    return [];
  }

  return map(investments, investment => {
    return {
      fundName: investment.name || investment.fundName,
      accountNumber: getAccountNumberByTRPCode(investment.trpCode, accounts),
      recordKeepingCode: getRKCodeByTRPCode(investment.trpCode, details),
      dollarAmount: isFundLaterFlow
        ? 0
        : investment.amount || investment.fundingAmount,
      percentageAmount:
        investment.percent || investment.fundingAmountPercentage,
      contributionYear: 'CURRENT_YEAR'
    };
  });
};
/* eslint-enable complexity */
